/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT Licence.
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
const msalConfig = {
  auth: {
    clientId: `${process.env.REACT_APP_AZURE_CLIENT_ID}`, // This is the ONLY mandatory field that you need to supply.
    authority: `${process.env.REACT_APP_AZURE_AUTHORITY}`, // Defaults to "https://login.microsoftonline.com/common"
    redirectUri: window.location.protocol + "//" + window.location.host, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
    knownAuthorities: [`${process.env.REACT_APP_AZURE_KNOWN_AUTHORITIES}`], // used for B2B apps
  },
  cache: {
    cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean,
      ) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

interface LoginRequest {
  scopes: string[];
  state: string | undefined;
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 *
 * state: dynamically added to send state to azure and is returned in login addEventCallback
 */
const loginRequest: LoginRequest = {
  scopes: ["email", `${process.env.REACT_APP_AZURE_SCOPE}`],
  state: undefined,
};

interface ProtectedResources {
  api: {
    endpoint: string;
    scopes: string[];
  };
}

/**
 * Add entries here for different scopes and hosts
 * Note: resource path names are placed in services
 */
const protectedResources: ProtectedResources = {
  api: {
    endpoint: `${process.env.REACT_APP_BASE_URL}`,
    scopes: [process.env.REACT_APP_B2C_SCOPE as string],
  },
};

export { msalConfig, loginRequest, protectedResources };
